import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { initialState } from './initialState';
import { ApiExError } from '@shared';

const advisorSlice = createSlice({
  name: 'advisor',
  initialState: initialState.advisor,
  reducers: {
    fetchAdvisorByAdvisorIdRequest(_state, _action: PayloadAction<string>) {
      return initialState.advisor;
    },
    fetchAdvisorByAdvisorIdResponse(state, action: PayloadAction<Advisor>) {
      state.data = action.payload;
    },
    fetchAdvisorByAdvisorIdFailure(_state, _action: PayloadAction<ApiExError>) {
      return initialState.advisor;
    },
    putAdvisorByAdvisorIdRequest(_state, _action: PayloadAction<Advisor>) {
      return initialState.advisor;
    },
    putAdvisorByAdvisorIdResponse(state, action: PayloadAction<Advisor>) {
      state.data = action.payload;
    },
    putAdvisorByAdvisorIdFailure(_state, _action: PayloadAction<ApiExError>) {
      return initialState.advisor;
    },
    resetAdvisorSettings(state) {
      state.advisorSettings.isLoading = false;
      state.advisorSettings.status = null;
    },
    postAdvisorUpdatesByAdvisorIdRequest(
      state,
      _action: PayloadAction<AdvisorProfile>,
    ) {
      state.advisorSettings.isLoading = true;
      state.advisorSettings.status = null;
    },
    postAdvisorUpdatesByAdvisorIdResponse(
      state,
      action: PayloadAction<Advisor>,
    ) {
      state.data = action.payload;
      state.advisorSettings.isLoading = false;
      state.advisorSettings.status = 'complete';
    },
    postAdvisorUpdatesByAdvisorId(state, _action: PayloadAction<ApiExError>) {
      state.advisorSettings.isLoading = false;
      state.advisorSettings.status = 'error';
    },
    resetAdvisorUpdatesStatus(state, action: PayloadAction<AdvisorUpdateKeys>) {
      state.advisorUpdates[action.payload] = null;
    },
    putAdvisorReferralSourceByAdvisorIdRequest(
      state,
      _action: PayloadAction<PutAdvisorReferralSourceBodyProps>,
    ) {
      state.advisorUpdates.putAdvisorReferralSource = 'loading';
    },
    putAdvisorReferralSourceByAdvisorIdResponse(
      state,
      action: PayloadAction<AdvisorReferralSource[]>,
    ) {
      state.advisorUpdates.putAdvisorReferralSource = 'complete';
    },
    putAdvisorReferralSourceByAdvisorIdFailure(
      state,
      _action: PayloadAction<ApiExError>,
    ) {
      state.advisorUpdates.putAdvisorReferralSource = 'error';
    },
    putClientAccessLinkEmailToClientRequest(
      state,
      _action: PayloadAction<ClientIdEmailProps>,
    ) {
      state.advisorUpdates.putClientAccessLinkEmail = 'loading';
    },
    putClientAccessLinkEmailToClientResponse(
      state,
      action: PayloadAction<ClientIdEmailResponse>,
    ) {
      if (action.payload.status === 'unsubscribed') {
        state.advisorUpdates.putClientAccessLinkEmail = 'unsubscribed';
      } else {
        state.advisorUpdates.putClientAccessLinkEmail = 'complete';
      }
    },
    putClientAccessLinkEmailToClientFailure(
      state,
      _action: PayloadAction<ApiExError>,
    ) {
      state.advisorUpdates.putClientAccessLinkEmail = 'error';
    },
    putAdvisorOrganizationByAdvisorIdRequest(
      state,
      _action: PayloadAction<PutAdvisorOrganizationByAdvisorIdBodyProps>,
    ) {
      state.advisorUpdates.putAdvisorOrganization = 'loading';
    },
    putAdvisorOrganizationByAdvisorIdResponse(
      state,
      action: PayloadAction<AdvisorOrganization>,
    ) {
      state.advisorUpdates.putAdvisorOrganization = 'complete';
    },
    putAdvisorOrganizationByAdvisorIdFailure(
      state,
      _action: PayloadAction<ApiExError>,
    ) {
      state.advisorUpdates.putAdvisorOrganization = 'error';
    },
  },
});

export const {
  fetchAdvisorByAdvisorIdRequest,
  fetchAdvisorByAdvisorIdResponse,
  fetchAdvisorByAdvisorIdFailure,
  putAdvisorByAdvisorIdRequest,
  putAdvisorByAdvisorIdResponse,
  putAdvisorByAdvisorIdFailure,
  postAdvisorUpdatesByAdvisorIdRequest,
  postAdvisorUpdatesByAdvisorIdResponse,
  postAdvisorUpdatesByAdvisorId,
  putClientAccessLinkEmailToClientRequest,
  putClientAccessLinkEmailToClientResponse,
  putClientAccessLinkEmailToClientFailure,
  resetAdvisorSettings,
  resetAdvisorUpdatesStatus,
  putAdvisorReferralSourceByAdvisorIdRequest,
  putAdvisorReferralSourceByAdvisorIdResponse,
  putAdvisorReferralSourceByAdvisorIdFailure,
  putAdvisorOrganizationByAdvisorIdRequest,
  putAdvisorOrganizationByAdvisorIdResponse,
  putAdvisorOrganizationByAdvisorIdFailure,
} = advisorSlice.actions;

export const advisorReducer = advisorSlice.reducer;
